import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.warn('Missing Supabase env vars. Check your .env file.');
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ── Entries ──────────────────────────────────────────────

export async function fetchPublicEntries({ type, search, limit = 100 } = {}) {
  // List view only needs display fields — prompt/text_output fetched on demand
  let q = supabase
    .from('entries')
    .select('id, title, type, model, tags, file_url, file_name, source_url, is_public, created_at, user_id, profiles!entries_user_id_fkey(username, display_name)')
    .eq('is_public', true)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (type && type !== 'all') q = q.eq('type', type);
  if (search) q = q.or(`title.ilike.%${search}%,model.ilike.%${search}%`);
  const { data, error } = await q;
  if (error) throw error;
  return data;
}

export async function fetchMyEntries(userId, folderId) {
  // Explicit columns only — prompt and text_output fetched on demand in DetailPanel
  let q = supabase
    .from('entries')
    .select('id, title, type, model, tags, is_public, file_url, file_name, source_url, folder_id, created_at, user_id, profiles!entries_user_id_fkey(username, display_name), folders(id, name, color)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(200);  // cap at 200 — paginate if needed later
  if (folderId === 'none') q = q.is('folder_id', null);
  else if (folderId) q = q.eq('folder_id', folderId);
  const { data, error } = await q;
  if (error) throw error;
  return data;
}

export async function fetchEntryById(id) {
  const { data, error } = await supabase
    .from('entries')
    .select('*, profiles!entries_user_id_fkey(username, display_name), folders(id, name, color)')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
}

// Fetch a single entry for public sharing — only returns if is_public = true
export async function fetchPublicEntryById(id) {
  const { data, error } = await supabase
    .from('entries')
    .select('id, title, type, model, prompt, text_output, notes, tags, source_url, is_public, created_at, profiles!entries_user_id_fkey(username, display_name)')
    .eq('id', id)
    .eq('is_public', true)
    .single();
  if (error) return null;
  return data;
}

export async function insertEntry(entry) {
  const { data, error } = await supabase
    .from('entries').insert(entry).select().single();
  if (error) throw error;
  return data;
}

export async function updateEntry(id, fields, userId) {
  if (!userId) throw new Error('Not authenticated');
  // Always include user_id in the WHERE so users can only update their own
  const { data, error } = await supabase
    .from('entries').update(fields).eq('id', id).eq('user_id', userId).select().single();
  if (error) throw error;
  if (!data) throw new Error('Entry not found or not authorised');
  return data;
}


// ── Storage cleanup helpers ───────────────────────────────────────────────────

// Extract the storage path from a full Supabase public URL
// e.g. "https://xxx.supabase.co/storage/v1/object/public/entry-files/abc/123.jpg"
// → "abc/123.jpg"
function storagePathFromUrl(url) {
  if (!url) return null;
  try {
    const marker = '/object/public/entry-files/';
    const idx = url.indexOf(marker);
    if (idx === -1) return null;
    return decodeURIComponent(url.slice(idx + marker.length).split('?')[0]);
  } catch { return null; }
}

async function deleteStorageFile(path) {
  if (!path) return;
  try {
    await supabase.storage.from('entry-files').remove([path]);
  } catch (e) {
    // Non-fatal — log and continue. File may already be gone.
    console.warn('[storage] Could not delete', path, e?.message);
  }
}

async function deleteStorageFiles(paths) {
  const valid = (paths || []).filter(Boolean);
  if (!valid.length) return;
  try {
    await supabase.storage.from('entry-files').remove(valid);
  } catch (e) {
    console.warn('[storage] Could not delete files', e?.message);
  }
}

export async function deleteEntry(id, userId) {
  if (!userId) throw new Error('Not authenticated');
  // Fetch file_url AND verify ownership before deleting
  const { data: entry } = await supabase
    .from('entries').select('file_url, user_id').eq('id', id).single();
  if (!entry) throw new Error('Entry not found');
  if (entry.user_id !== userId) throw new Error('Not authorised to delete this entry');
  const { error } = await supabase.from('entries').delete().eq('id', id).eq('user_id', userId);
  if (error) throw error;
  if (entry?.file_url) await deleteStorageFile(storagePathFromUrl(entry.file_url));
}

export async function bulkDeleteEntries(ids, userId) {
  if (!ids?.length) return;
  if (!userId) throw new Error('Not authenticated');
  // Fetch file_urls AND verify all entries belong to this user
  const { data: entries } = await supabase
    .from('entries').select('id, file_url, user_id').in('id', ids);
  const unauthorised = (entries || []).filter(e => e.user_id !== userId);
  if (unauthorised.length) throw new Error('Not authorised to delete one or more entries');
  // Only delete rows this user owns — belt and suspenders
  const { error } = await supabase.from('entries').delete().in('id', ids).eq('user_id', userId);
  if (error) throw error;
  const paths = (entries || []).map(e => storagePathFromUrl(e.file_url)).filter(Boolean);
  if (paths.length) await deleteStorageFiles(paths);
}

export async function togglePublic(id, isPublic, userId) {
  if (!userId) throw new Error('Not authenticated');
  const { error } = await supabase
    .from('entries').update({ is_public: isPublic }).eq('id', id).eq('user_id', userId);
  if (error) throw error;
}

// ── Folders ──────────────────────────────────────────────

export async function fetchFolders(userId) {
  const { data, error } = await supabase
    .from('folders')
    .select('*, entries(count), folder_items(count)')
    .eq('user_id', userId)
    .is('team_id', null)  // personal folders only — team folders have team_id set
    .order('name');
  if (error) throw error;
  // Normalise count: entries count + folder_items count
  return (data || []).map(f => ({
    ...f,
    total_count: (f.entries?.[0]?.count || 0) + (f.folder_items?.[0]?.count || 0),
  }));
}

export async function createFolder(userId, name, color) {
  const { data, error } = await supabase
    .from('folders')
    .insert({ user_id: userId, name, color })
    .select().single();
  if (error) throw error;
  return data;
}

export async function updateFolder(id, fields) {
  const { data, error } = await supabase
    .from('folders').update(fields).eq('id', id).select().single();
  if (error) throw error;
  return data;
}

export async function deleteFolder(id) {
  const { error } = await supabase.from('folders').delete().eq('id', id);
  if (error) throw error;
}

// ── Folder junction — skills & playbooks ────────────────────────────────

export async function addItemToFolder(folderId, itemType, itemId, userId) {
  const { error } = await supabase
    .from('folder_items')
    .upsert({ folder_id: folderId, item_type: itemType, item_id: itemId, user_id: userId },
             { onConflict: 'folder_id,item_type,item_id' });
  if (error) throw error;
}

export async function removeItemFromFolder(folderId, itemType, itemId) {
  const { error } = await supabase
    .from('folder_items')
    .delete()
    .eq('folder_id', folderId)
    .eq('item_type', itemType)
    .eq('item_id', itemId);
  if (error) throw error;
}

export async function fetchFolderItems(folderId, userId) {
  // Returns all skills and playbooks in a folder
  const { data, error } = await supabase
    .from('folder_items')
    .select('item_type, item_id, created_at')
    .eq('folder_id', folderId)
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchItemFolders(itemType, itemId, userId) {
  // Returns all folders that contain a given item
  const { data, error } = await supabase
    .from('folder_items')
    .select('folder_id')
    .eq('item_type', itemType)
    .eq('item_id', itemId)
    .eq('user_id', userId);
  if (error) throw error;
  return (data || []).map(r => r.folder_id);
}

export async function hasUserInstalledItem(itemType, itemId, userId) {
  // Check if user has their own copy (forked_from = itemId)
  const table = itemType === 'playbook' ? 'skill_packs' : 'skills';
  const { count } = await supabase
    .from(table)
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('forked_from', itemId);
  return (count || 0) > 0;
}

export async function setItemFolders(itemType, itemId, userId, folderIds) {
  // Replace all folder memberships for an item in one call
  // 1. Remove existing
  await supabase
    .from('folder_items')
    .delete()
    .eq('item_type', itemType)
    .eq('item_id', itemId)
    .eq('user_id', userId);
  // 2. Insert new
  if (folderIds.length === 0) return;
  const rows = folderIds.map(folder_id => ({ folder_id, item_type: itemType, item_id: itemId, user_id: userId }));
  const { error } = await supabase.from('folder_items').insert(rows);
  if (error) throw error;
}

export async function moveItemToFolder(itemType, itemId, folderId, userId) {
  // Move item to a single folder (removes from all others first)
  await setItemFolders(itemType, itemId, userId, folderId ? [folderId] : []);
}

export async function fetchFolderAllItems(folderId, userId) {
  // Fetch all three item types for a folder in parallel
  const [
    { data: entries },
    { data: folderItemRows },
  ] = await Promise.all([
    // Entries via folder_id column (old mechanism, kept for compat)
    supabase
      .from('entries')
      .select('*, profiles!entries_user_id_fkey(username, display_name)')
      .eq('user_id', userId)
      .eq('folder_id', folderId)
      .order('created_at', { ascending: false }),
    // Skills + playbooks via junction table
    supabase
      .from('folder_items')
      .select('item_type, item_id')
      .eq('folder_id', folderId)
      .eq('user_id', userId),
  ]);

  const skillIds    = (folderItemRows || []).filter(r => r.item_type === 'skill').map(r => r.item_id);
  const playbookIds = (folderItemRows || []).filter(r => r.item_type === 'playbook').map(r => r.item_id);

  const [{ data: skills }, { data: packs }] = await Promise.all([
    skillIds.length
      ? supabase.from('skills').select('id, title, description, category, type, model, tags, slug, variables, is_public, use_count, fork_count, forked_from, user_id, created_at').in('id', skillIds)
      : Promise.resolve({ data: [] }),
    playbookIds.length
      ? supabase.from('skill_packs').select('id, title, description, category, cover_color, cover_image_url, slug, is_public, install_count, forked_from, user_id, created_at').in('id', playbookIds)
      : Promise.resolve({ data: [] }),
  ]);

  return {
    entries:   (entries || []).map(e => ({ ...e, item_kind: 'entry' })),
    skills:    (skills  || []).map(s => ({ ...s, item_kind: 'skill' })),
    playbooks: (packs   || []).map(p => ({ ...p, item_kind: 'playbook' })),
  };
}

export async function fetchSkillsInFolder(folderId, userId) {
  const items = await fetchFolderItems(folderId, userId);
  const skillIds   = items.filter(i => i.item_type === 'skill').map(i => i.item_id);
  const playbookIds = items.filter(i => i.item_type === 'playbook').map(i => i.item_id);

  const [{ data: skills }, { data: packs }] = await Promise.all([
    skillIds.length
      ? supabase.from('skills').select('id, title, description, category, type, model, tags, slug, variables, is_public, use_count, fork_count, forked_from, user_id, created_at').in('id', skillIds)
      : Promise.resolve({ data: [] }),
    playbookIds.length
      ? supabase.from('skill_packs').select('id, title, description, category, cover_color, cover_image_url, slug, is_public, install_count, forked_from, user_id, created_at').in('id', playbookIds)
      : Promise.resolve({ data: [] }),
  ]);

  return {
    skills:    (skills    || []).map(s => ({ ...s, item_type: 'skill' })),
    playbooks: (packs     || []).map(p => ({ ...p, item_type: 'playbook' })),
  };
}

export async function moveEntriesToFolder(entryIds, folderId) {
  const { error } = await supabase
    .from('entries')
    .update({ folder_id: folderId || null })
    .in('id', entryIds);
  if (error) throw error;
}

// ── File upload ──────────────────────────────────────────

export async function uploadFile(file, userId, onProgress) {
  const { compressImage, validateVideoSize } = await import('./compress.js');

  let toUpload = file;

  if (file.type.startsWith('image/')) {
    onProgress && onProgress({ phase: 'compressing', percent: 0 });
    toUpload = await compressImage(file);
    onProgress && onProgress({ phase: 'compressing', percent: 100 });
  } else if (file.type.startsWith('video/')) {
    validateVideoSize(file);
  }

  const ext  = toUpload.name.split('.').pop();
  const path = `${userId}/${Date.now()}.${ext}`;

  // Use XMLHttpRequest so we get real upload progress events
  const url = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/entry-files/${path}`;
  const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token || anonKey;

  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('x-upsert', 'false');
    if (toUpload.type) xhr.setRequestHeader('content-type', toUpload.type);

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        onProgress && onProgress({ phase: 'uploading', percent });
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        try {
          const err = JSON.parse(xhr.responseText);
          reject(new Error(err.message || `Upload failed: ${xhr.status}`));
        } catch {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
    xhr.send(toUpload);
  });

  onProgress && onProgress({ phase: 'done', percent: 100 });

  const { data } = supabase.storage.from('entry-files').getPublicUrl(path);
  return { url: data.publicUrl, name: file.name };
}

// ── Admin ────────────────────────────────────────────────

export async function fetchAllEntries() {
  const { data, error } = await supabase
    .from('entries')
    .select('*, profiles!entries_user_id_fkey(username, display_name)')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function adminDeleteEntry(id) {
  const { error } = await supabase.from('entries').delete().eq('id', id);
  if (error) throw error;
}

export async function fetchAllProfiles() {
  const { data, error } = await supabase
    .from('profiles').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function setUserRole(userId, role) {
  const { error } = await supabase
    .from('profiles').update({ role }).eq('id', userId);
  if (error) throw error;
}

export async function setUserPlan(userId, plan) {
  const { error } = await supabase
    .from('profiles')
    .update({ plan, trial_expires_at: null }) // clear trial when granting/revoking explicit plan
    .eq('id', userId);
  if (error) throw error;
}

export async function setUserTrial(userId, expiresAt) {
  // expiresAt: ISO string to start a trial, or null to end it
  const { error } = await supabase
    .from('profiles')
    .update({ trial_expires_at: expiresAt || null })
    .eq('id', userId);
  if (error) throw error;
}

export async function banUser(userId) {
  // Set a banned flag — we use role='banned' as the signal
  const { error } = await supabase
    .from('profiles').update({ role: 'banned' }).eq('id', userId);
  if (error) throw error;
}

export async function fetchAdminStats() {
  const [entriesRes, profilesRes] = await Promise.all([
    supabase.from('entries').select('id, is_public, created_at', { count: 'exact' }),
    supabase.from('profiles').select('id, plan, role, created_at', { count: 'exact' }),
  ]);
  if (entriesRes.error) throw entriesRes.error;
  if (profilesRes.error) throw profilesRes.error;

  const entries  = entriesRes.data  || [];
  const profiles = profilesRes.data || [];

  const now   = new Date();
  const day   = new Date(now - 86400000);
  const week  = new Date(now - 7 * 86400000);
  const month = new Date(now - 30 * 86400000);

  return {
    entries: {
      total:   entries.length,
      public:  entries.filter(e => e.is_public).length,
      private: entries.filter(e => !e.is_public).length,
      today:   entries.filter(e => new Date(e.created_at) > day).length,
      week:    entries.filter(e => new Date(e.created_at) > week).length,
    },
    users: {
      total:  profiles.length,
      free:   profiles.filter(p => p.plan === 'free' && p.role !== 'banned').length,
      pro:    profiles.filter(p => p.plan === 'pro').length,
      admin:  profiles.filter(p => p.role === 'admin').length,
      banned: profiles.filter(p => p.role === 'banned').length,
      newThisMonth: profiles.filter(p => new Date(p.created_at) > month).length,
    },
  };
}

// ── Reports ──────────────────────────────────────────────────

export async function submitReport(entryId, reporterId, reason, details) {
  const { error } = await supabase
    .from('reports')
    .insert({ entry_id: entryId, reporter_id: reporterId, reason, details: details || null });
  if (error) throw error;
}

export async function hasUserReported(entryId, userId) {
  const { data } = await supabase
    .from('reports')
    .select('id')
    .eq('entry_id', entryId)
    .eq('reporter_id', userId)
    .single();
  return !!data;
}

export async function countTodayReports(userId) {
  const since = new Date();
  since.setHours(0, 0, 0, 0);
  const { count } = await supabase
    .from('reports')
    .select('id', { count: 'exact', head: true })
    .eq('reporter_id', userId)
    .gte('created_at', since.toISOString());
  return count || 0;
}

export async function fetchFlaggedEntries() {
  const { data, error } = await supabase
    .from('entries')
    .select('*, profiles!entries_user_id_fkey(username, display_name)')
    .or('is_flagged.eq.true,report_count.gt.0')
    .order('report_count', { ascending: false });
  if (error) throw error;
  return data;
}

export async function fetchEntryReports(entryId) {
  const { data, error } = await supabase
    .from('reports')
    .select('id, reason, details, created_at')
    .eq('entry_id', entryId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function dismissReports(entryId) {
  // Delete all reports for this entry and clear flags
  const { error: delErr } = await supabase
    .from('reports')
    .delete()
    .eq('entry_id', entryId);
  if (delErr) throw delErr;

  const { error: upErr } = await supabase
    .from('entries')
    .update({ report_count: 0, is_flagged: false, flagged_at: null, is_public: true })
    .eq('id', entryId);
  if (upErr) throw upErr;
}

export async function adminHideEntry(entryId) {
  const { error } = await supabase
    .from('entries')
    .update({ is_public: false, hidden_by_mod: true })
    .eq('id', entryId);
  if (error) throw error;
}

export async function adminRestoreEntry(entryId) {
  const { error } = await supabase
    .from('entries')
    .update({
      is_public: true, is_flagged: false,
      flagged_at: null, report_count: 0, hidden_by_mod: false,
    })
    .eq('id', entryId);
  if (error) throw error;
}

export async function fetchPendingReportCount() {
  const { count } = await supabase
    .from('entries')
    .select('id', { count: 'exact', head: true })
    .eq('is_flagged', true);
  return count || 0;
}

// ── Skills ───────────────────────────────────────────────────

export async function fetchMySkills(userId) {
  // Exclude prompt — loaded on demand when user opens a skill to use/edit it
  const { data, error } = await supabase
    .from('skills')
    .select('id, title, description, category, type, model, tags, slug, variables, is_public, use_count, fork_count, forked_from, output_example, output_image_url, prompt_image_url, reference_url, reference_name, user_id, created_at, profiles(username, display_name)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(300);
  if (error) throw error;
  return data;
}

// fetchPublicSkills kept for internal compatibility (SkillsPage forked tab etc.)
export async function fetchPublicSkills({ type, search } = {}) {
  let q = supabase
    .from('skills')
    .select('*, profiles(username, display_name)')
    .eq('is_public', true)
    .order('use_count', { ascending: false });
  if (type && type !== 'all') q = q.eq('type', type);
  if (search) q = q.or(`title.ilike.%${search}%,description.ilike.%${search}%,tags.cs.{${search}}`);
  const { data, error } = await q;
  if (error) throw error;
  return data;
}

export async function fetchSkillById(id) {
  const { data, error } = await supabase
    .from('skills').select('*, profiles(username, display_name)').eq('id', id).single();
  if (error) throw error;
  return data;
}

export async function insertSkill(skill) {
  const { data, error } = await supabase
    .from('skills').insert(skill).select().single();
  if (error) throw error;
  return data;
}

export async function updateSkill(id, fields, userId) {
  if (!userId) throw new Error('Not authenticated — please refresh and try again');
  const { data, error } = await supabase
    .from('skills').update(fields).eq('id', id).eq('user_id', userId).select().single();
  if (error) throw error;

  // If a thumbnail was set, sync it to the marketplace listing too
  // so Browse shows the same image without needing a Creator Dashboard edit
  if (fields.output_image_url && data?.user_id) {
    await supabase
      .from('marketplace_listings')
      .update({ cover_image_url: fields.output_image_url })
      .eq('item_id', id)
      .eq('user_id', data.user_id)
      .eq('status', 'published');
    // No error check — listing may not exist yet, that's fine
  }

  return data;
}

export async function deleteSkill(id, userId) {
  if (!userId) throw new Error('Not authenticated');
  const { data: skill } = await supabase
    .from('skills')
    .select('output_image_url, prompt_image_url, reference_url, user_id')
    .eq('id', id).single();
  if (skill?.user_id && skill.user_id !== userId) throw new Error('Not authorised to delete this skill');
  const { error } = await supabase.from('skills').delete().eq('id', id).eq('user_id', userId);
  if (error) throw error;
  // Clean up storage files
  await deleteStorageFiles([
    storagePathFromUrl(skill?.output_image_url),
    storagePathFromUrl(skill?.prompt_image_url),
    storagePathFromUrl(skill?.reference_url),
  ]);
}

export async function incrementSkillUseCount(id) {
  Promise.resolve(supabase.rpc('increment_skill_use_count', { skill_id: id })).catch(() => {});
}

// Install a skill from marketplace — forks without title suffix
export async function installSkillFromMarketplace(skill, userId) {
  const { data, error } = await supabase
    .from('skills')
    .insert({
      user_id:        userId,
      title:          skill.title,
      description:    skill.description,
      prompt:         skill.prompt,
      variables:      skill.variables || [],
      model:          skill.model,
      type:           skill.type,
      category:       skill.category,
      tags:           skill.tags || [],
      output_example: skill.output_example || null,
      is_public:      false,
      forked_from:    skill.id,
      use_count:      0,
    })
    .select().single();
  if (error) throw error;

  // Notify the skill owner (fire and forget — never block the install)
  if (skill.user_id && skill.user_id !== userId) {
    supabase.from('notifications').insert({
      user_id:    skill.user_id,
      type:       'install',
      title:      'Someone installed your skill',
      body:       `"${skill.title}" was installed`,
      item_id:    skill.id,
      item_type:  'skill',
      item_title: skill.title,
      actor_id:   userId,
    }).then(() => {}).catch(() => {});
  }

  return data;
}

export async function forkSkill(skill, userId) {
  const { data, error } = await supabase
    .from('skills')
    .insert({
      user_id:        userId,
      title:          skill.title + ' (fork)',
      description:    skill.description,
      prompt:         skill.prompt,
      variables:      skill.variables,
      model:          skill.model,
      type:           skill.type,
      tags:           skill.tags,
      category:       skill.category,
      is_public:      false,          // forks are private by default
      forked_from:    skill.id,
      reference_url:  skill.reference_url  || null,
      reference_name: skill.reference_name || null,
    })
    .select().single();
  if (error) throw error;
  return data;
}

// ── Teams ─────────────────────────────────────────────────────

export async function createTeam({ name, slug, plan = 'starter' }) {
  const seatLimits = { starter: 5, growth: 15, agency: 999 };

  // Use getSession() to get user ID from active session token
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user?.id) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('teams')
    .insert({
      name, slug,
      owner_id: session.user.id,
      plan,
      seat_limit: seatLimits[plan] || 3,
    })
    .select().single();

  if (error) throw error;
  return data;
}

export async function fetchMyTeams(userId) {
  const { data, error } = await supabase
    .from('team_members')
    .select('role, joined_at, teams(id, name, slug, avatar_url, plan, seat_limit, settings, owner_id)')
    .eq('user_id', userId)
    .order('joined_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(row => ({ ...row.teams, my_role: row.role, joined_at: row.joined_at }));
}

export async function fetchTeam(teamId) {
  const { data, error } = await supabase
    .from('teams')
    .select('*')
    .eq('id', teamId)
    .single();
  if (error) throw error;
  return data;
}

export async function updateTeam(teamId, fields) {
  const { data, error } = await supabase
    .from('teams').update(fields).eq('id', teamId).select().single();
  if (error) throw error;
  return data;
}

export async function deleteTeam(teamId) {
  const { error } = await supabase.from('teams').delete().eq('id', teamId);
  if (error) throw error;
}

// ── Team members ──────────────────────────────────────────────

export async function fetchTeamMembers(teamId) {
  const { data, error } = await supabase
    .from('team_members_view')
    .select('*')
    .eq('team_id', teamId)
    .order('joined_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function fetchTeamMemberStats(teamId) {
  const { data, error } = await supabase
    .from('team_member_stats')
    .select('*')
    .eq('team_id', teamId)
    .order('entry_count', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function updateMemberRole(teamId, userId, role) {
  const { error } = await supabase
    .from('team_members')
    .update({ role })
    .eq('team_id', teamId)
    .eq('user_id', userId);
  if (error) throw error;
}

export async function removeMember(teamId, userId) {
  const { error } = await supabase
    .from('team_members')
    .delete()
    .eq('team_id', teamId)
    .eq('user_id', userId);
  if (error) throw error;
}

// ── Team invites ──────────────────────────────────────────────

export async function sendTeamInvite(teamId, email, role = 'member') {
  const { data: session } = await supabase.auth.getSession();
  const userId = session.session?.user?.id;

  // Generate a secure random token client-side
  const tokenBytes = new Uint8Array(24);
  crypto.getRandomValues(tokenBytes);
  const token = Array.from(tokenBytes).map(b => b.toString(16).padStart(2, '0')).join('');

  // Expires 7 days from now
  const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

  const { data, error } = await supabase
    .from('team_invites')
    .upsert(
      { team_id: teamId, invited_by: userId, email, role, status: 'pending', token, expires_at: expiresAt },
      { onConflict: 'team_id,email' }
    )
    .select().single();
  if (error) throw error;
  return data;
}

export async function fetchTeamInvites(teamId) {
  const { data, error } = await supabase
    .from('team_invites')
    .select('*')
    .eq('team_id', teamId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchInviteByToken(token) {
  const { data, error } = await supabase
    .from('team_invites')
    .select('*, teams(id, name, slug, avatar_url)')
    .eq('token', token)
    .single();
  if (error) throw error;
  return data;
}

export async function acceptInvite(token, userId) {
  // Get the invite
  const invite = await fetchInviteByToken(token);
  if (!invite) throw new Error('Invite not found');
  if (invite.status !== 'pending') throw new Error('This invite has already been used or expired');
  if (new Date(invite.expires_at) < new Date()) throw new Error('This invite has expired');

  // Check seat limit — count across ALL teams owned by this team's owner
  // Seat limit lives on the owner's profile (account-wide pool)
  const team = await fetchTeam(invite.team_id);

  // Get owner's profile for their account-wide seat limit
  const { data: ownerProfile } = await supabase
    .from('profiles')
    .select('teams_seat_limit, teams_billing_status')
    .eq('id', team.owner_id)
    .single();

  if (!ownerProfile || ownerProfile.teams_billing_status !== 'active') {
    throw new Error('The team owner does not have an active Teams subscription');
  }

  if (ownerProfile.teams_seat_limit !== 999) {
    // Count all members across all teams owned by this owner
    const { data: ownerTeams } = await supabase
      .from('teams')
      .select('id')
      .eq('owner_id', team.owner_id);

    const teamIds = (ownerTeams || []).map(t => t.id);

    const { count: totalSeats } = await supabase
      .from('team_members')
      .select('id', { count: 'exact', head: true })
      .in('team_id', teamIds);

    if (totalSeats >= ownerProfile.teams_seat_limit) {
      throw new Error(`Seat limit reached. Your ${ownerProfile.teams_seat_limit}-seat plan is full across all your teams. Upgrade to add more members.`);
    }
  }

  // Add member
  const { error: memberError } = await supabase
    .from('team_members')
    .upsert({ team_id: invite.team_id, user_id: userId, role: invite.role, invited_by: invite.invited_by },
             { onConflict: 'team_id,user_id' });
  if (memberError) throw memberError;

  // Mark invite accepted
  await supabase.from('team_invites').update({ status: 'accepted' }).eq('token', token);

  return invite.teams;
}

export async function revokeInvite(inviteId) {
  const { error } = await supabase.from('team_invites').delete().eq('id', inviteId);
  if (error) throw error;
}

// ── Team entries ──────────────────────────────────────────────

export async function fetchTeamEntries(teamId, { type, search, status } = {}) {
  let q = supabase
    .from('entries')
    .select('*, profiles!entries_user_id_fkey(username, display_name), folders(id, name, color)')
    .eq('team_id', teamId)
    .order('created_at', { ascending: false });

  if (type && type !== 'all') q = q.eq('type', type);
  if (search) q = q.or(`title.ilike.%${search}%,prompt.ilike.%${search}%`);
  if (status) q = q.eq('team_status', status);

  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function submitEntryToTeam(entryId, teamId) {
  const { error } = await supabase
    .from('entries')
    .update({ team_id: teamId, team_status: 'pending' })
    .eq('id', entryId);
  if (error) throw error;
}

export async function approveTeamEntry(entryId) {
  const userId = (await supabase.auth.getUser()).data.user?.id;
  const { error } = await supabase
    .from('entries')
    .update({ team_status: 'approved', team_approved_by: userId, team_approved_at: new Date().toISOString() })
    .eq('id', entryId);
  if (error) throw error;
}

export async function rejectTeamEntry(entryId) {
  const { error } = await supabase
    .from('entries')
    .update({ team_status: 'rejected', team_id: null })
    .eq('id', entryId);
  if (error) throw error;
}

// ── Team folders ──────────────────────────────────────────────

// ── Team skills ───────────────────────────────────────────────

export async function fetchTeamSkills(teamId) {
  const { data, error } = await supabase
    .from('skills')
    .select('*, profiles!entries_user_id_fkey(username, display_name)')
    .eq('team_id', teamId)
    .order('use_count', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function createTeamSkill(teamId, skill) {
  const userId = (await supabase.auth.getUser()).data.user?.id;
  const { data, error } = await supabase
    .from('skills')
    .insert({ ...skill, team_id: teamId, user_id: userId, is_public: false })
    .select().single();
  if (error) throw error;
  return data;
}

export async function lockTeamSkill(skillId, locked) {
  const { error } = await supabase
    .from('skills').update({ team_locked: locked }).eq('id', skillId);
  if (error) throw error;
}

// ── Team activity ─────────────────────────────────────────────

export async function fetchTeamActivity(teamId, limit = 50) {
  const { data, error } = await supabase
    .from('team_activity')
    .select('*, profiles!entries_user_id_fkey(username, display_name, avatar_url)')
    .eq('team_id', teamId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function logActivity(teamId, action, targetId = null, targetType = null, meta = {}) {
  // Fire and forget
  supabase.rpc('log_team_activity', {
    p_team_id: teamId, p_action: action,
    p_target_id: targetId, p_target_type: targetType, p_meta: meta,
  }).catch(() => {});
}

// ── Team slug availability ────────────────────────────────────

export async function checkSlugAvailable(slug) {
  const { data } = await supabase
    .from('teams').select('id').eq('slug', slug).single();
  return !data; // true = available
}

export async function setActiveTeam(userId, teamId) {
  const { error } = await supabase
    .from('profiles').update({ active_team_id: teamId }).eq('id', userId);
  if (error) throw error;
}

// ── API Keys ──────────────────────────────────────────────────

export async function generateApiKey() {
  // Generate key entirely client-side using Web Crypto API
  // The Edge Function approach had persistent JWT issues — client-side is equally secure
  // since only the SHA-256 hash is stored in the database, never the plaintext key

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Generate 24 random bytes → 48 hex chars
  const randomBytes = new Uint8Array(24);
  crypto.getRandomValues(randomBytes);
  const randomHex    = Array.from(randomBytes).map(b => b.toString(16).padStart(2, '0')).join('');
  const plaintextKey = `cpg_${randomHex}`;
  const keyPrefix    = plaintextKey.slice(0, 12);

  // SHA-256 hash — only this gets stored
  const hashBuf = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(plaintextKey)
  );
  const keyHash = Array.from(new Uint8Array(hashBuf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');

  // Delete existing key then insert new one
  await supabase.from('api_keys').delete().eq('user_id', user.id);

  const { error: insertError } = await supabase.from('api_keys').insert({
    user_id:    user.id,
    key_hash:   keyHash,
    key_prefix: keyPrefix,
    name:       'My API key',
  });

  if (insertError) throw insertError;

  return {
    key:        plaintextKey,   // returned once, never stored
    key_prefix: keyPrefix,
    created_at: new Date().toISOString(),
  };
}

export async function fetchApiKey(userId) {
  const { data, error } = await supabase
    .from('api_keys')
    .select('key_prefix, name, last_used_at, created_at')
    .eq('user_id', userId)
    .single();
  if (error && error.code !== 'PGRST116') throw error; // PGRST116 = not found
  return data || null;
}

export async function revokeApiKey(userId) {
  const { error } = await supabase
    .from('api_keys')
    .delete()
    .eq('user_id', userId);
  if (error) throw error;
}

// ── Team Collaboration ────────────────────────────────────────

// ── Share an entry to a team ──────────────────────────────────
export async function shareEntryToTeam(entryId, teamId, teamFolderId = null) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user?.id) throw new Error('Not authenticated');

  // Get team settings + check if user is admin/owner (admins bypass approval)
  const [{ data: team }, { data: memberRow }] = await Promise.all([
    supabase.from('teams').select('settings, owner_id').eq('id', teamId).single(),
    supabase.from('team_members').select('role').eq('team_id', teamId).eq('user_id', session.user.id).single(),
  ]);

  const approvalRequired = team?.settings?.approval_required ?? false;
  const isAdminOrOwner   = team?.owner_id === session.user.id ||
                           memberRow?.role === 'admin' ||
                           memberRow?.role === 'owner';

  // Admins and owners always get auto-approved regardless of team setting
  const status = (approvalRequired && !isAdminOrOwner) ? 'pending' : 'approved';

  const shareData = {
    team_id:   teamId,
    entry_id:  entryId,
    shared_by: session.user.id,
    status,
  };
  if (teamFolderId) shareData.team_folder_id = teamFolderId;

  const { data, error } = await supabase
    .from('team_entry_shares')
    .upsert(shareData, { onConflict: 'team_id,entry_id' })
    .select().single();

  if (error) throw error;
  return { share: data, pending: status === 'pending' };
}

// ── Unshare an entry from a team ─────────────────────────────
export async function unshareEntryFromTeam(entryId, teamId) {
  const { error } = await supabase
    .from('team_entry_shares')
    .delete()
    .eq('entry_id', entryId)
    .eq('team_id', teamId);
  if (error) throw error;
}

// ── Check which teams an entry is shared to ───────────────────
export async function fetchEntryShares(entryId) {
  const { data, error } = await supabase
    .from('team_entry_shares')
    .select('id, team_id, status, team_folder_id, teams(id, name)')
    .eq('entry_id', entryId);
  if (error) throw error;
  return data || [];
}

// ── Fetch team feed (approved shares + entry details) ─────────
export async function fetchTeamFeed(teamId, { status = 'approved' } = {}) {
  // Step 1: get shares
  let q = supabase
    .from('team_entry_shares')
    .select('id, status, created_at, rejected_note, shared_by, entry_id')
    .eq('team_id', teamId)
    .order('created_at', { ascending: false });

  if (status === 'pending')  q = q.eq('status', 'pending');
  if (status === 'approved') q = q.eq('status', 'approved');

  const { data: shares, error: sharesErr } = await q;
  console.log('[fetchTeamFeed] shares:', JSON.stringify(shares), 'error:', sharesErr?.message);
  if (sharesErr) throw sharesErr;
  if (!shares?.length) return [];

  // Step 2: fetch the actual entries
  const entryIds = shares.map(s => s.entry_id);
  const { data: entries, error: entriesErr } = await supabase
    .from('entries')
    .select('id, title, type, model, prompt, text_output, notes, tags, file_url, is_public, created_at, user_id, profiles!entries_user_id_fkey(username, display_name)')
    .in('id', entryIds);

  console.log('[fetchTeamFeed] entries:', JSON.stringify(entries), 'error:', entriesErr?.message);
  if (entriesErr) throw entriesErr;

  const entryMap = Object.fromEntries((entries || []).map(e => [e.id, e]));

  return shares.map(share => {
    const entry = entryMap[share.entry_id] || {};
    return {
      shareId:      share.id,
      shareStatus:  share.status,
      teamFolderId: share.team_folder_id || null,
      sharedAt:     share.created_at,
      rejectedNote: share.rejected_note,
      sharedBy:     null,
      ...entry,
    };
  }).filter(r => r.id); // only rows where entry was found
}

// ── Approve a share ───────────────────────────────────────────
export async function approveShare(shareId) {
  const { data: { session } } = await supabase.auth.getSession();
  const { error } = await supabase
    .from('team_entry_shares')
    .update({
      status:      'approved',
      approved_by: session?.user?.id,
      approved_at: new Date().toISOString(),
    })
    .eq('id', shareId);
  if (error) throw error;
}

// ── Reject a share ────────────────────────────────────────────
export async function rejectShare(shareId, note = '') {
  const { error } = await supabase
    .from('team_entry_shares')
    .update({ status: 'rejected', rejected_note: note })
    .eq('id', shareId);
  if (error) throw error;
}

// ── Fetch pending shares for a team (admins only) ─────────────
export async function fetchPendingShares(teamId) {
  return fetchTeamFeed(teamId, { status: 'pending' });
}

// ── Team folders ──────────────────────────────────────────────
export async function fetchTeamFolders(teamId) {
  const { data, error } = await supabase
    .from('folders')
    .select('id, name, color, team_id, user_id, created_by, created_at')
    .eq('team_id', teamId)
    .order('name', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function createTeamFolder(teamId, name, color = '#7f77dd') {
  const { data: { session } } = await supabase.auth.getSession();
  const { data, error } = await supabase
    .from('folders')
    .insert({
      user_id:    session.user.id,
      team_id:    teamId,
      name,
      color,
      created_by: session.user.id,
    })
    .select().single();
  if (error) throw error;
  return data;
}

export async function deleteTeamFolder(folderId) {
  const { error } = await supabase.from('folders').delete().eq('id', folderId);
  if (error) throw error;
}

export async function updateTeamFolder(folderId, updates) {
  const { data, error } = await supabase
    .from('folders')
    .update(updates)
    .eq('id', folderId)
    .select().single();
  if (error) throw error;
  return data;
}

// ── Move a share to a team folder ─────────────────────────────
export async function moveShareToFolder(shareId, teamFolderId) {
  const { error } = await supabase
    .from('team_entry_shares')
    .update({ team_folder_id: teamFolderId })
    .eq('id', shareId);
  if (error) throw error;
}

// ── Fetch team feed filtered by team folder ───────────────────
export async function fetchTeamFolderEntries(teamId, teamFolderId) {
  const { data, error } = await supabase
    .from('team_entry_shares')
    .select(`
      id, status, team_folder_id, created_at,
      entries!inner(
        id, title, type, model, prompt, text_output, notes,
        tags, file_url, is_public, created_at, user_id,
        profiles!entries_user_id_fkey(username, display_name)
      ),
      profiles!team_entry_shares_shared_by_fkey(username, display_name)
    `)
    .eq('team_id', teamId)
    .eq('team_folder_id', teamFolderId)
    .eq('status', 'approved')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(row => ({
    shareId: row.id,
    shareStatus: row.status,
    sharedAt: row.created_at,
    sharedBy: row.profiles,
    ...row.entries,
  }));
}

// ── Check if current user has already shared an entry to a team ─
export async function getShareStatus(entryId, teamId) {
  const { data, error } = await supabase
    .from('team_entry_shares')
    .select('id, status')
    .eq('entry_id', entryId)
    .eq('team_id', teamId)
    .single();
  if (error && error.code !== 'PGRST116') throw error;
  return data || null;
}

// ── Playbooks ───────────────────────────────────────────────

export async function fetchMyPlaybooks(userId) {
  // Skills within playbooks: only need display fields for the card UI
  const { data, error } = await supabase
    .from('skill_packs')
    .select(`
      id, title, description, category, cover_color, cover_image_url, slug,
      is_public, install_count, forked_from, user_id, created_at,
      profiles!skill_packs_user_id_fkey(username, display_name),
      skill_pack_skills(
        position,
        skills(id, title, description, category, type, model, variables, slug)
      )
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data || []).map(normalizePlaybook);
}

export async function fetchPublicPlaybooks({ search, category } = {}) {
  let q = supabase
    .from('skill_packs')
    .select(`
      *,
      profiles!skill_packs_user_id_fkey(username, display_name),
      skill_pack_skills(
        position,
        skills(*)
      )
    `)
    .eq('is_public', true)
    .order('install_count', { ascending: false });
  if (category && category !== 'all') q = q.eq('category', category);
  if (search) q = q.or(`title.ilike.%${search}%,description.ilike.%${search}%`);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map(normalizePlaybook);
}

export async function fetchPlaybookById(id) {
  const { data, error } = await supabase
    .from('skill_packs')
    .select(`
      *,
      profiles!skill_packs_user_id_fkey(username, display_name),
      skill_pack_skills(position, skills(*))
    `)
    .eq('id', id)
    .single();
  if (error) throw error;
  return normalizePlaybook(data);
}

export async function fetchPlaybookBySlug(slug) {
  const { data, error } = await supabase
    .from('skill_packs')
    .select(`
      *,
      profiles!skill_packs_user_id_fkey(username, display_name),
      skill_pack_skills(
        position,
        skills(*)
      )
    `)
    .eq('slug', slug)
    .single();
  if (error) throw error;
  return normalizePlaybook(data);
}

function normalizePlaybook(pack) {
  const skills = (pack.skill_pack_skills || [])
    .sort((a, b) => a.position - b.position)
    .map(s => s.skills)
    .filter(Boolean);
  return { ...pack, skills, skill_pack_skills: undefined };
}

export async function createPlaybook(fields) {
  const { data: { session } } = await supabase.auth.getSession();
  const { data, error } = await supabase
    .from('skill_packs')
    .insert({ ...fields, user_id: session.user.id })
    .select().single();
  if (error) throw error;
  return data;
}

export async function updatePlaybook(packId, fields, userId) {
  if (!userId) throw new Error('Not authenticated');
  const { data, error } = await supabase
    .from('skill_packs')
    .update({ ...fields, updated_at: new Date().toISOString() })
    .eq('id', packId)
    .eq('user_id', userId)
    .select().single();
  if (error) throw error;
  if (!data) throw new Error('Playbook not found or not authorised');
  return data;
}

export async function deletePlaybook(playbookId, userId) {
  if (!userId) throw new Error('Not authenticated — please refresh and try again');
  const { data: pack } = await supabase
    .from('skill_packs')
    .select('cover_image_url, user_id')
    .eq('id', playbookId).single();
  if (pack?.user_id && pack.user_id !== userId) throw new Error('Not authorised to delete this playbook');
  const { error } = await supabase.from('skill_packs').delete().eq('id', playbookId).eq('user_id', userId);
  if (error) throw error;
  await deleteStorageFile(storagePathFromUrl(pack?.cover_image_url));
}

export async function addSkillToPlaybook(playbookId, skillId, position = 0) {
  const { error } = await supabase
    .from('skill_pack_skills')
    .upsert({ pack_id: playbookId, skill_id: skillId, position }, { onConflict: 'pack_id,skill_id' });
  if (error) throw error;
}

export async function removeSkillFromPlaybook(packId, skillId) {
  const { error } = await supabase
    .from('skill_pack_skills')
    .delete()
    .eq('pack_id', packId)
    .eq('skill_id', skillId);
  if (error) throw error;
}

export async function reorderPlaybookSkills(packId, skillIds) {
  // Update positions for all skills in order
  const updates = skillIds.map((skillId, i) => ({
    pack_id: packId, skill_id: skillId, position: i,
  }));
  const { error } = await supabase
    .from('skill_pack_skills')
    .upsert(updates, { onConflict: 'pack_id,skill_id' });
  if (error) throw error;
}

// ── Unified Marketplace Queries ─────────────────────────────────────────────
// These replace fetchPublicSkills/fetchPublicPlaybooks for the Marketplace page.
// Queries public skills + playbooks directly, enriched with marketplace_listings
// metadata where available. No listing row required to appear.

// Free install limits — keep in sync with FREE_SKILL_INSTALL/FREE_PLAYBOOK_INSTALL in AuthContext.jsx
export const FREE_SKILL_LIMIT    = 3;
export const FREE_PLAYBOOK_LIMIT = 1;

export async function fetchMarketplaceItems({
  category = 'all',
  typeFilter = 'all',
  sort = 'popular',
  search = '',
  limit = 40,  // reduced from 80 — load more on scroll if needed
} = {}) {
  // Card view only needs display fields — prompt loaded on demand in ItemModal
  let q = supabase
    .from('marketplace_items')
    .select('id, item_type, title, description, category, cover_color, cover_image_url, tags, install_count, avg_rating, rating_count, featured, listing_id, user_id, created_at, profiles(username, display_name)');

  if (typeFilter !== 'all') q = q.eq('item_type', typeFilter);
  if (category  !== 'all') q = q.eq('category', category);
  if (search) q = q.or(`title.ilike.%${search}%,description.ilike.%${search}%`);
  if (sort === 'popular') q = q.order('install_count', { ascending: false });
  if (sort === 'newest')  q = q.order('created_at',    { ascending: false });
  if (sort === 'rated')   q = q.order('avg_rating',    { ascending: false });
  q = q.limit(limit);

  const { data, error } = await q;

  // View not created yet — fall back to direct queries
  if (error && (error.code === '42P01' || error.message?.includes('marketplace_items'))) {
    return fetchMarketplaceItemsFallback({ category, typeFilter, sort, search, limit });
  }
  if (error) throw error;
  return data || [];
}

// Fallback: query skills + skill_packs directly (no view required)
async function fetchMarketplaceItemsFallback({ category, typeFilter, sort, search, limit }) {
  const results = [];

  if (typeFilter !== 'playbook') {
    let q = supabase
      .from('skills')
      .select('*, profiles(username, display_name)')
      .eq('is_public', true)
      .is('forked_from', null);
    if (category !== 'all') q = q.eq('category', category);
    if (search)  q = q.or(`title.ilike.%${search}%,description.ilike.%${search}%,tags.cs.{${search}}`);
    if (sort === 'popular') q = q.order('use_count', { ascending: false });
    if (sort === 'newest')  q = q.order('created_at', { ascending: false });
    q = q.limit(limit);
    const { data } = await q;
    (data || []).forEach(s => results.push({ ...s, item_type: 'skill', install_count: s.use_count || 0, view_count: 0, avg_rating: 0, rating_count: 0, featured: false, listing_id: null }));
  }

  if (typeFilter !== 'skill') {
    let q = supabase
      .from('skill_packs')
      .select('*, profiles!skill_packs_user_id_fkey(username, display_name)')
      .eq('is_public', true)
      .is('forked_from', null);
    if (category !== 'all') q = q.eq('category', category);
    if (search)  q = q.or(`title.ilike.%${search}%,description.ilike.%${search}%`);
    if (sort === 'popular') q = q.order('install_count', { ascending: false });
    if (sort === 'newest')  q = q.order('created_at', { ascending: false });
    q = q.limit(limit);
    const { data } = await q;
    (data || []).forEach(p => results.push({ ...p, item_type: 'playbook', view_count: 0, avg_rating: 0, rating_count: 0, featured: false, listing_id: null }));
  }

  // Sort combined results
  if (sort === 'popular') results.sort((a, b) => (b.install_count||0) - (a.install_count||0));
  if (sort === 'newest')  results.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  return results.slice(0, limit);
}

export async function fetchMarketplaceFeatured() {
  const { data, error } = await supabase
    .from('marketplace_items')
    .select('id, item_type, title, description, category, cover_color, cover_image_url, tags, install_count, avg_rating, rating_count, featured, listing_id, user_id, created_at, profiles(username, display_name)')
    .eq('featured', true)
    .order('featured_order', { ascending: true })
    .limit(6);
  if (error) throw error;
  return data || [];
}

export async function fetchMarketplaceCreator(username) {
  // Look up profile by username OR display_name
  let resolvedProfile = null;

  const { data: byUsername } = await supabase
    .from('profiles')
    .select('id, username, display_name, bio, avatar_url')
    .eq('username', username)
    .maybeSingle();
  resolvedProfile = byUsername;

  if (!resolvedProfile) {
    const { data: byDisplay } = await supabase
      .from('profiles')
      .select('id, username, display_name, bio, avatar_url')
      .ilike('display_name', username)
      .maybeSingle();
    resolvedProfile = byDisplay || null;
  }

  if (!resolvedProfile) return { profile: null, items: [] };

  // Try unified view first, fall back to direct skills query
  const { data: viewData, error: viewError } = await supabase
    .from('marketplace_items')
    .select('*, profiles(username, display_name)')
    .eq('user_id', resolvedProfile.id)
    .order('install_count', { ascending: false });

  if (!viewError) {
    return { profile: resolvedProfile, items: viewData || [] };
  }

  // Fallback: query skills + playbooks directly (view may not exist yet)
  const { data: skills } = await supabase
    .from('skills')
    .select('*, profiles(username, display_name)')
    .eq('user_id', resolvedProfile.id)
    .eq('is_public', true)
    .is('forked_from', null)
    .order('use_count', { ascending: false });

  const { data: packs } = await supabase
    .from('skill_packs')
    .select('*, profiles!skill_packs_user_id_fkey(username, display_name)')
    .eq('user_id', resolvedProfile.id)
    .eq('is_public', true)
    .is('forked_from', null)
    .order('install_count', { ascending: false });

  const items = [
    ...(skills || []).map(s => ({ ...s, item_type: 'skill', install_count: s.use_count || 0, avg_rating: 0, rating_count: 0, listing_id: null })),
    ...(packs  || []).map(p => ({ ...p, item_type: 'playbook', avg_rating: 0, rating_count: 0, listing_id: null })),
  ];

  return { profile: resolvedProfile, items };
}

export async function getUserFreeInstallCounts(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('free_skill_installs, free_playbook_installs')
    .eq('id', userId)
    .single();
  if (error) return { free_skill_installs: 0, free_playbook_installs: 0 };
  return data;
}

export async function incrementFreeSkillInstalls(userId) {
  const { data, error } = await supabase
    .rpc('increment_free_skill_installs', { p_user_id: userId });
  if (error) throw error;
  return data;
}

export async function incrementFreePlaybookInstalls(userId) {
  const { data, error } = await supabase
    .rpc('increment_free_playbook_installs', { p_user_id: userId });
  if (error) throw error;
  return data;
}

// ── Marketplace (listing enrichment) ────────────────────────────────────────

export async function fetchMarketplaceListings({ category, sort = 'popular', search, limit = 40 } = {}) {
  let q = supabase
    .from('marketplace_listings')
    .select(`
      *,
      profiles!marketplace_listings_user_id_fkey(username, display_name)
    `)
    .eq('status', 'published');

  if (category && category !== 'all') q = q.eq('category', category);
  if (search) q = q.or(`title.ilike.%${search}%,description.ilike.%${search}%`);

  if (sort === 'popular')  q = q.order('install_count', { ascending: false });
  if (sort === 'newest')   q = q.order('created_at',    { ascending: false });
  if (sort === 'rated')    q = q.order('avg_rating',    { ascending: false });

  q = q.limit(limit);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function fetchFeaturedListings() {
  const { data, error } = await supabase
    .from('marketplace_listings')
    .select(`*, profiles!marketplace_listings_user_id_fkey(username, display_name)`)
    .eq('status', 'published')
    .eq('featured', true)
    .order('featured_order', { ascending: true })
    .limit(6);
  if (error) throw error;
  return data || [];
}

export async function fetchListingsByCreator(username) {
  // First get user_id from username
  const { data: profile } = await supabase
    .from('profiles').select('id, username, display_name, bio, avatar_url')
    .eq('username', username).single();
  if (!profile) return { profile: null, listings: [] };

  const { data, error } = await supabase
    .from('marketplace_listings')
    .select('*')
    .eq('user_id', profile.id)
    .eq('status', 'published')
    .order('install_count', { ascending: false });
  if (error) throw error;
  return { profile, listings: data || [] };
}

export async function publishListing({ userId, itemType, itemId, title, description, category, coverColor, coverImageUrl, tags }) {
  // Check if listing already exists for this item
  const { data: existing } = await supabase
    .from('marketplace_listings')
    .select('id')
    .eq('item_id', itemId)
    .eq('user_id', userId)
    .single();

  let result;
  if (existing) {
    const { data, error } = await supabase
      .from('marketplace_listings')
      .update({ title, description, category, cover_color: coverColor, cover_image_url: coverImageUrl || null, tags: tags || [], status: 'published' })
      .eq('id', existing.id)
      .select().single();
    if (error) throw error;
    result = data;
  } else {
    const { data, error } = await supabase
      .from('marketplace_listings')
      .insert({ user_id: userId, item_type: itemType, item_id: itemId, title, description, category, cover_color: coverColor, cover_image_url: coverImageUrl || null, tags: tags || [], status: 'published' })
      .select().single();
    if (error) throw error;
    result = data;
  }

  // Sync cover image back to the source skill/playbook row so it shows everywhere
  if (coverImageUrl) {
    const table = itemType === 'playbook' ? 'skill_packs' : 'skills';
    const field = itemType === 'playbook' ? 'cover_image_url' : 'output_image_url';
    await supabase.from(table).update({ [field]: coverImageUrl }).eq('id', itemId).eq('user_id', userId);
  }

  return result;
}

export async function unpublishListing(listingId, userId) {
  const { error } = await supabase
    .from('marketplace_listings')
    .update({ status: 'draft' })
    .eq('id', listingId)
    .eq('user_id', userId);
  if (error) throw error;
  // Note: cover_image_url is kept so re-publishing is seamless.
  // Files only cleaned when the listing is hard-deleted (deleteListing).
}

export async function deleteListing(listingId, userId) {
  // Fetch cover before hard-deleting
  const { data: listing } = await supabase
    .from('marketplace_listings')
    .select('cover_image_url')
    .eq('id', listingId)
    .eq('user_id', userId)
    .single();
  const { error } = await supabase
    .from('marketplace_listings')
    .delete()
    .eq('id', listingId)
    .eq('user_id', userId);
  if (error) throw error;
  await deleteStorageFile(storagePathFromUrl(listing?.cover_image_url));
}

export async function submitRating(listingId, userId, rating, review = '') {
  const { data, error } = await supabase
    .from('marketplace_ratings')
    .upsert({ listing_id: listingId, user_id: userId, rating, review }, { onConflict: 'listing_id,user_id' })
    .select().single();
  if (error) throw error;

  // Notify the listing owner
  supabase
    .from('marketplace_listings')
    .select('user_id, title, item_id, item_type')
    .eq('id', listingId)
    .single()
    .then(({ data: listing }) => {
      if (!listing || listing.user_id === userId) return;
      supabase.from('notifications').insert({
        user_id:    listing.user_id,
        type:       review ? 'review' : 'rating',
        title:      review ? 'Someone reviewed your skill' : 'New rating on your skill',
        body:       review
          ? `${rating}★ — "${review.slice(0, 80)}${review.length > 80 ? '…' : ''}"`
          : `${rating} star rating on "${listing.title}"`,
        item_id:    listing.item_id,
        item_type:  listing.item_type,
        item_title: listing.title,
        actor_id:   userId,
      }).then(() => {}).catch(() => {});
    }).catch(() => {});

  return data;
}

export async function fetchUserRating(listingId, userId) {
  const { data } = await supabase
    .from('marketplace_ratings')
    .select('rating, review')
    .eq('listing_id', listingId)
    .eq('user_id', userId)
    .single();
  return data || null;
}

export async function incrementListingView(listingId) {
  await Promise.resolve(
    supabase.rpc('increment_listing_view_count', { listing_id: listingId })
  ).catch(() => {});
}

export async function incrementListingInstall(listingId) {
  await Promise.resolve(
    supabase.rpc('increment_listing_install_count', { listing_id: listingId })
  ).catch(() => {});
}

export async function installPlaybook(playbook, userId) {
  // Generate a unique slug suffix to avoid conflicts with the source
  const slugSuffix = `-${userId.slice(0, 6)}`;

  // ── Step 1: Fork each skill into the user's library ──────────
  const forkedSkills = [];
  for (const skill of (playbook.skills || [])) {
    const { data, error } = await supabase
      .from('skills')
      .insert({
        user_id:        userId,
        title:          skill.title,
        description:    skill.description,
        prompt:         skill.prompt,
        variables:      skill.variables || [],
        model:          skill.model,
        type:           skill.type,
        category:       skill.category,
        tags:           skill.tags || [],
        output_example: skill.output_example || null,
        // Omit slug — let it be null to avoid unique constraint conflicts
        // User can set their own slug if they edit the skill
        is_public:      false,
        forked_from:    skill.id,
        use_count:      0,
      })
      .select()
      .single();
    if (!error && data) forkedSkills.push(data);
    else if (error) console.warn('Skill fork error:', error.message, skill.title);
  }

  // ── Step 2: Create a playbook copy owned by the user ─────────
  // Use a unique slug by appending part of the userId to avoid conflicts
  const playbookInsert = {
    user_id:       userId,
    title:         playbook.title,
    description:   playbook.description,
    category:      playbook.category,
    cover_color:   playbook.cover_color,
    is_public:     false,
    install_count: 0,
  };

  // Only set slug if source has one — append suffix to avoid uniqueness conflict
  if (playbook.slug) {
    playbookInsert.slug = playbook.slug + slugSuffix;
  }

  // Only set forked_from if the column exists (requires migration)
  // Wrapped in try so it degrades gracefully if column not yet added
  let newPlaybook = null;
  try {
    const insertWithFork = { ...playbookInsert, forked_from: playbook.id };
    const { data, error } = await supabase
      .from('skill_packs')
      .insert(insertWithFork)
      .select()
      .single();

    if (error) {
      // forked_from column may not exist yet — retry without it
      if (error.code === '42703' || error.message?.includes('forked_from')) {
        const { data: data2, error: error2 } = await supabase
          .from('skill_packs')
          .insert(playbookInsert)
          .select()
          .single();
        if (error2) console.warn('Playbook install error:', error2.message);
        else newPlaybook = data2;
      } else {
        console.warn('Playbook install error:', error.message);
      }
    } else {
      newPlaybook = data;
    }
  } catch (e) {
    console.warn('Playbook install exception:', e.message);
  }

  // ── Step 3: Link forked skills to the new playbook copy ───────
  if (newPlaybook && forkedSkills.length) {
    const links = forkedSkills.map((s, i) => ({
      pack_id:  newPlaybook.id,
      skill_id: s.id,
      position: i,
    }));
    const { error: linkError } = await supabase
      .from('skill_pack_skills')
      .insert(links);
    if (linkError) console.warn('Skill link error:', linkError.message);
  }

  // ── Step 4: Increment install count on source playbook ────────
  await Promise.resolve(
    supabase.rpc('increment_pack_install_count', { pack_id: playbook.id })
  ).catch(() => {});

  // Notify the playbook owner (don't block on this)
  if (playbook.user_id && playbook.user_id !== userId) {
    supabase.from('notifications').insert({
      user_id:    playbook.user_id,
      type:       'install',
      title:      'Someone installed your playbook',
      body:       `"${playbook.title}" was installed`,
      item_id:    playbook.id,
      item_type:  'playbook',
      item_title: playbook.title,
      actor_id:   userId,
    }).then(() => {}).catch(() => {});
  }

  // Return both so the UI can update skills + playbooks lists
  return {
    skills:   forkedSkills,
    playbook: newPlaybook ? { ...newPlaybook, skills: forkedSkills } : null,
  };
}

// ── Share link for a marketplace item ────────────────────────────────────
export async function fetchItemBySlug(slug, type = 'skill') {
  const table = type === 'playbook' ? 'skill_packs' : 'skills';
  const { data, error } = await supabase
    .from(table)
    .select('*, profiles(username, display_name)')
    .eq('slug', slug)
    .eq('is_public', true)
    .single();
  if (error) return null;
  return data;
}

// ── Notifications ─────────────────────────────────────────────────────────

export async function fetchNotifications(userId, limit = 20) {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function fetchUnreadNotificationCount(userId) {
  const { count, error } = await supabase
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('read', false);
  if (error) return 0;
  return count || 0;
}

export async function markNotificationsRead(userId) {
  const { error } = await supabase
    .from('notifications')
    .update({ read: true })
    .eq('user_id', userId)
    .eq('read', false);
  if (error) throw error;
}

export async function markOneNotificationRead(notificationId) {
  const { error } = await supabase
    .from('notifications')
    .update({ read: true })
    .eq('id', notificationId);
  if (error) throw error;
}
